import { FC } from "react";
import { animated, useSpring } from "react-spring/web.cjs";

import { alpha, styled } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { TransitionProps } from "@mui/material/transitions";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem, treeItemClasses, TreeItemProps } from "@mui/x-tree-view/TreeItem";

const PaletteTransitionComponent = (props: TransitionProps) => {
	const style = useSpring({
		from: {
			opacity: 0,
			transform: "translate3d(20px,0,0)",
			filter: "blur(0)",
		},
		to: {
			opacity: props.in ? 1 : 0,
			transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
			filter: "blur(0)",
		},
	});

	return (
		<animated.div style={style}>
			<Collapse {...props} />
		</animated.div>
	);
};

export const StyledTreeView = styled(SimpleTreeView)({
	flexGrow: 1,
	margin: "0.5rem",
});

export interface StyledTreeItemProps extends TreeItemProps {
	isDragging: boolean;
}

const StyledTreeItemRoot = styled(TreeItem, {
	shouldForwardProp: (prop) => prop !== "isDragging",
})<StyledTreeItemProps>(({ theme, isDragging }) => ({
	opacity: isDragging ? 0.5 : 1,

	[`& .${treeItemClasses.content}`]: {
		minHeight: 28,
		paddingTop: 0,
		paddingBottom: 0,
		paddingLeft: theme.spacing(0.5),
		paddingRight: theme.spacing(0.5),
	},

	[`& .${treeItemClasses.label}`]: {
		fontSize: "0.8125rem",
		lineHeight: 1.2,
	},

	[`& .${treeItemClasses.iconContainer}`]: {
		width: 24,
		minWidth: 24,
		marginRight: theme.spacing(0.5),
	},

	[`& .${treeItemClasses.iconContainer} .close`]: {
		opacity: 0.3,
	},

	[`& .${treeItemClasses.groupTransition}`]: {
		marginLeft: theme.spacing(1),
		paddingLeft: theme.spacing(1),
		borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
	},
}));

export const StyledTreeItem: FC<StyledTreeItemProps> = ({ isDragging, ...props }) => {
	return (
		<StyledTreeItemRoot {...props} isDragging={isDragging} slots={{ groupTransition: PaletteTransitionComponent }} />
	);
};
